import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@shared/hooks/useAuthStore';
import { env, isRuntimeConfig } from '@shared/runtimeEnv';

export interface MatchUpdatePayload {
  kind?:
    | 'match:update'
    | 'event:add'
    | 'event:remove'
    | 'clock:state'
    | string;
  matchId?: string;
  status?: string;
  phase?: string;
  homeScore?: number | null;
  awayScore?: number | null;
  minute?: number | null;
  liveStartedAt?: string | null;
  pausedAt?: string | null;
  pausedAccumMs?: number;
  serverNow?: string;
  event?: {
    id: string;
    type: string;
    minute: number;
    team: 'home' | 'away';
    playerName: string;
    detail: string | null;
  };
  eventId?: string;
  score?: { home: number | null; away: number | null };
  [key: string]: unknown;
}

const tenantSlug = env('TENANT_SLUG') || 'lanciano';
// Build-time: VITE_WS_BASE_URL vuoto (stessa origin) resta valido, solo 'undefined' cade su localhost.
const wsBase = isRuntimeConfig()
  ? env('WS_BASE_URL')
  : (import.meta.env.VITE_WS_BASE_URL ?? 'http://localhost:3000');

/**
 * Subscribe to /matches WS namespace and join the room for a single match.
 * Returns the latest payload + connection status.
 */
export function useMatchSocket(matchId: string | null): {
  connected: boolean;
  lastUpdate: MatchUpdatePayload | null;
} {
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<MatchUpdatePayload | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!matchId) return;

    const socket = io(`${wsBase}/matches`, {
      transports: ['websocket'],
      extraHeaders: {
        'X-Tenant-ID': tenantSlug,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('joinMatch', { tenantSlug, matchId });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('matchUpdate', (payload: MatchUpdatePayload) => {
      setLastUpdate(payload);
    });

    return () => {
      socket.emit('leaveMatch', { tenantSlug, matchId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [matchId, token]);

  return { connected, lastUpdate };
}
